import { type ButtonHTMLAttributes, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibField } from '@chewy/kib-fields-new-react'
import { useClickOutside } from '@chewy/kib-helpers-react'
import { KibChevronDownIcon } from '@chewy/kib-icons-react'
import KibTag from '@chewy/kib-tag-react'
import classNames from 'classnames'
import {
  type AppointmentEventType,
  EventTypeName,
  LanguageUtils,
  Utils,
} from '@pbt/pbt-ui-components'

import { ColorVariantToTheme } from '~/constants/colors'
import { AppointmentStateColorMap } from '~/constants/event'
import { getAppointmentId } from '~/store/reducers/soap'
import { getTimetableEvent } from '~/store/reducers/timetable'
import { useFilteredAppointmentStatuses } from '~/utils/appointmentStatusUtils'
import { useEventType } from '~/utils/useEventType'

import { useAppointmentStatusChange } from '../useAppointmentStatusChange'

import '@chewy/kib-tag-styles/dist/kib-tag.css'
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import styles from './AppointmentStatusSelect.module.scss'

const AppointmentStatusSelect = () => {
  const { t } = useTranslation(['Common'])

  const [isStatusSelectOpen, setIsStatusSelectOpen] = useState(false)
  const [localSelectedStatusId, setLocalSelectedStatusId] = useState<
    string | null
  >(null)
  const statusSelectRef = useRef(null)

  const allStatuses: AppointmentEventType['states'] = useEventType(
    EventTypeName.Appointment,
    'states',
  )
  const appointmentId = useSelector(getAppointmentId)
  const appointment = useSelector(getTimetableEvent(appointmentId))
  const { appointmentStatus, onHandleStatusChange } =
    useAppointmentStatusChange(appointment)

  // Use local state for immediate updates, fall back to appointmentStatus.value
  const appointmentStatusId = localSelectedStatusId || appointmentStatus.value

  const selectedOption = Utils.findById(appointmentStatusId, allStatuses) || {}

  const filteredStatuses = useFilteredAppointmentStatuses(allStatuses)
  const statusesWithCurrent = [
    ...filteredStatuses,
    ...(selectedOption?.id &&
    !filteredStatuses.some((s) => s.id === selectedOption.id)
      ? [selectedOption]
      : []),
  ]

  const handleSelectStatus = (statusId: string) => {
    setLocalSelectedStatusId(statusId)
    onHandleStatusChange(statusId)
    setIsStatusSelectOpen(false)
  }

  useClickOutside(statusSelectRef, () => {
    setIsStatusSelectOpen(false)
  })

  // Reset local state when appointmentStatus.value updates to match
  useEffect(() => {
    if (
      localSelectedStatusId &&
      appointmentStatus.value === localSelectedStatusId
    ) {
      setLocalSelectedStatusId(null)
    }
  }, [appointmentStatus.value, localSelectedStatusId])

  return (
    <div className={styles.statusSelectContainer} ref={statusSelectRef}>
      <KibField
        hiddenLabel
        className={styles.kibField}
        label={t('Common:APPOINTMENT_STATUS')}
        renderControl={({ ...props }) => (
          <KibButtonNew
            {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
            className={classNames(
              styles.statusSelectButton,
              styles[
                ColorVariantToTheme[
                  AppointmentStateColorMap[selectedOption?.name || '']
                ] || 'default'
              ],
            )}
            emphasis="tertiary"
            icon={<KibChevronDownIcon />}
            iconPosition="end"
            theme="utility"
            onClick={(event) => {
              event.stopPropagation()
              setIsStatusSelectOpen((prev) => !prev)
            }}
          >
            {LanguageUtils.getTranslatedFieldName(selectedOption)}
          </KibButtonNew>
        )}
        value={LanguageUtils.getTranslatedFieldName(selectedOption)}
      />
      {isStatusSelectOpen && (
        <ul className={styles.selectMenu} role="listbox">
          {statusesWithCurrent.map((status) => {
            const statusName = LanguageUtils.getTranslatedFieldName(status)

            return (
              <li
                aria-selected={selectedOption?.id === status.id}
                key={`${statusName}-status-select-option`}
                role="option"
                onClick={(event) => {
                  event.stopPropagation()
                  handleSelectStatus(status.id)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.stopPropagation()
                    handleSelectStatus(status.id)
                  }
                }}
              >
                <KibTag
                  className={styles.tag}
                  theme={
                    ColorVariantToTheme[AppointmentStateColorMap[statusName]] ||
                    'default'
                  }
                >
                  {statusName}
                </KibTag>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default AppointmentStatusSelect
